/**
 * ==========================================================================
 * Bộ lọc Hypothesis
 * ==========================================================================
 *
 * Luồng xử lý:
 *      1. Lấy toàn bộ product từ Shopify API.
 *      2. Chỉ giữ lại product có tag "Hypothesis".
 *      3. Lưu danh sách vào [HypothesisFilter.state.productList] để sử dụng cho bộ lọc.
 *      4. Đọc query string (?category=...) để khôi phục trạng thái filter.
 *      5. Áp dụng các điều kiện lọc:
 *          - Search (theo product title)
 *          - Category (product.product_type)
 *      6. Sắp xếp dữ liệu.
 *      7. Render danh sách Hypothesis theo từng Category.
 *
 * --------------------------------------------------------------------------
 * Render dữ liệu
 * --------------------------------------------------------------------------
 *      Chưa chọn Category:
 *          - Hiển thị tất cả Category.
 *          - Mỗi Category chỉ hiển thị:
 *              + 3 template mặc định. Quản lý tại: [HypothesisFilter.config.defaultPerPage: 3]
 *
 *      Đã chọn Category:
 *          - Chỉ hiển thị Category đang chọn.
 *          - Mỗi lần "Show more" hiển thị thêm:
 *              + 12 template. Quản lý tại: [HypothesisFilter.config.categoryPerPage: 12]
 *
 * --------------------------------------------------------------------------
 * Search
 * --------------------------------------------------------------------------
 *      - Tìm theo product.title.
 *      - Không phân biệt hoa/thường.
 *      - Chuẩn hóa chuỗi trước khi so khớp.
 *      - Hỗ trợ tìm theo nhiều từ khóa.
 *          Ví dụ:
 *              + "benefits"
 *                  → Khớp với mọi product có title chứa từ "benefits"
 *
 *              + "landing benefits"
 *                  → Chỉ khớp với title chứa cả "landing" và "benefits" (không bắt buộc đúng thứ tự)
 *
 * --------------------------------------------------------------------------
 * Show more
 * --------------------------------------------------------------------------
 *      Khi đang ở All Hypothesis:
 *          - Click "Show more" sẽ chuyển sang Category tương ứng bằng query string (?category=...)
 *
 *      Khi đang ở trang Category:
 *          - Click "Show more" sẽ render thêm template của Category đó.
 *          - Không render lại toàn bộ danh sách.
 *
 * --------------------------------------------------------------------------
 * Đồng bộ URL
 * --------------------------------------------------------------------------
 *      Category được lưu trên query string (?category=...) để:
 *          - Có thể reload hoặc chia sẻ URL.
 *          - Không cần reload khi đổi Category (History API).
 *
 * --------------------------------------------------------------------------
 * Sắp xếp dữ liệu. 
 *      - Quản lý tại: [HypothesisFilter.state.currentSort]
 *      - Mặc định đang là 'created-descending'
 * --------------------------------------------------------------------------
 *      - best-selling:
 *          Recommend → Tag Trending → Tag Featured → Product thường (Fallback theo ngày tạo mới nhất)
 *      - created-descending : Mới nhất trước.
 *      - created-ascending  : Cũ nhất trước.
 *      - title-ascending    : A → Z.
 *      - title-descending   : Z → A.
 *
 * --------------------------------------------------------------------------
 * Cache dữ liệu
 * --------------------------------------------------------------------------
 *      - Danh sách Hypothesis được lưu trong sessionStorage.
 *      - Thời gian cache: 10 phút.
 *      - Giảm số lần gọi Shopify API khi reload hoặc quay lại trang trong cùng session.
 *
 * ==========================================================================
 */

const HypothesisFilter = {
    config: {
        defaultPerPage: 3, // Số template mặc định ở All Template
        categoryPerPage: 12, // Số template hiển thị mỗi lần ở trang Tag

        apiLimit: 250, // Shopify products.json max limit
        filterQueryParam: 'category', // url dạng: /pages/hypothesis?category=product-spotlight

        industryTitleMap: {
            'Cta': 'CTA',
            'Faq Objections': 'FAQ Objections'
        },

        industriesTarget: '#js-industries-list',
        searchInputTarget: '#js-tpl-search-input',
        filterSelected: '#js-filter-selected'

    },

    state: {
        currentSort: 'created-descending',
        searchKeyword: '',
        filteredProducts: [], // Cache danh sách sau khi filter + sort
        productList: [],
        filterSelected: [],
        visibleItems: {},
        isCategoryPage: false
    },

    ui: {
        renderProductItem(product) {
            const tagsHtml = (product.tags || []).map((tag, index) => {
                const isRecommend = product.__tagsLower[index] === 'recommend';

                return `
                    <div class="tpl-card__badge ${isRecommend ? '!order-[0]' : ''}">
                        ${isRecommend ? TemplateIcon.recommendIcon : ''}
                        <span>${tag}</span>
                    </div>
                `;
            }).join('');

            const image = product.images?.[0];
            const imageSrc = HypothesisFilter.helpers.getResizeImage(image?.src, 640);

            return `
                <article class="tpl-card">
                    <div class="tpl-card__thumb">
                        ${tagsHtml ? `<div class="tpl-card__tags">${tagsHtml}</div>` : ''}

                        <div class="w-full relative">
                            <img 
                                src="${imageSrc}"
                                alt="${image?.alt || product.title}" 
                                width="${image?.width || ''}"
                                height="${image?.height || ''}"
                                loading="lazy"
                                decoding="async"
                                class="w-full h-full object-cover object-top m-auto block"
                            >
                        </div>

                        <div class="tpl-card__hover">
                            <a href="/pages/hypothesis/${product.handle}" class="tpl-card__preview">
                                ${TemplateIcon.previewIcon}
                                <span>Preview</span>
                            </a>

                            <a href="/pages/hypothesis/${product.handle}" class="tpl-card__use">                                
                                <span>View Details</span>
                                ${TemplateIcon.arrowIcon}
                            </a>
                        </div>
                    </div>

                    <div class="tpl-card__body">
                        <a href="/pages/hypothesis/${product.handle}"> 
                            <h3 class="tpl-card__title capitalize">${product.title}</h3>
                        </a>
                        
                        <div class="tpl-card__body-row"> <span>${product.product_type}</span> </div>
                    </div>
                </article>
            `;
        },

        renderCategories(products) {
            const { state, config } = HypothesisFilter;
            const { getDisplayIndustry } = HypothesisFilter.helpers;

            const container = document.querySelector('#js-product-categories');
            if (!container) return;

            if (!products.length) {
                container.innerHTML = ` 
                    <div class="tpl-empty">
                        <h3>No hypotheses match that search.</h3>
                        <p>Try a different keyword, clear filters, or browse all page types from the categories above.</p>
                        <button type="button" id="js-clear-filters">Clear filters</button>
                    </div>`;
                return;
            }

            const groupedProducts = products.reduce((acc, product) => {
                const type = product.product_type;

                if (!type) return acc;

                if (!acc[type]) {
                    acc[type] = [];
                }

                acc[type].push(product);

                return acc;
            }, {});

            const industries = Object.keys(groupedProducts).sort((a, b) => a.localeCompare(b));

            const perPage = state.isCategoryPage
                ? config.categoryPerPage
                : config.defaultPerPage;

            container.innerHTML = industries.map(industry => {
                const industryProducts = groupedProducts[industry];

                const visibleCount = Math.min(
                    state.visibleItems[industry] ?? perPage,
                    industryProducts.length
                );

                state.visibleItems[industry] = visibleCount;

                const visibleProducts = industryProducts.slice(0, visibleCount);

                const hasMore = industryProducts.length > visibleCount;

                const displayTitle = getDisplayIndustry(industry);

                return `
                    <div class="js-tpl-container tpl-section" data-type="${industry}">
                        <div class="tpl-section__head">
                            <h2 class="tpl-section__title">${displayTitle}</h2>
                            <span class="tpl-section__sub">
                                <strong>${industryProducts.length}</strong> templates · click any to preview
                            </span>
                        </div>

                        <div class="js-product-holder tpl-grid">
                            ${visibleProducts.map(product => this.renderProductItem(product)).join('')}
                        </div>

                        ${hasMore ? `
                            <div class="tpl-section__footer mt-7 text-center">
                                <button type="button" class="js-show-more btn btn--primary" style="line-height: normal;"
                                    data-industry="${industry}">
                                    <span>Show more </span>
                                    <span> ${TemplateIcon.arrowIcon}</span>
                                </button>
                            </div>
                        ` : ''}
                    </div>
                `;
            }).join('');
        },

        appendProducts(industry) {
            const { state, config } = HypothesisFilter;
            const { slugify } = HypothesisFilter.helpers;

            const section = document.querySelector(`.js-tpl-container[data-type="${industry}"]`);

            if (!section) return;

            const industrySlug = slugify(industry);

            const products = state.filteredProducts.filter(
                product => product.__typeSlug === industrySlug
            );

            const perPage = state.isCategoryPage
                ? config.categoryPerPage
                : config.defaultPerPage;

            const currentVisible = state.visibleItems[industry] ?? perPage;

            const nextProducts = products.slice(
                currentVisible,
                currentVisible + perPage
            );

            section.querySelector('.js-product-holder')
                .insertAdjacentHTML(
                    'beforeend',
                    nextProducts
                        .map(product => this.renderProductItem(product))
                        .join('')
                );

            const newVisible = currentVisible + nextProducts.length;

            state.visibleItems[industry] = newVisible;

            if (newVisible >= products.length) {
                section.querySelector('.tpl-section__footer')?.remove();
            }
        },

        updateTotalTemplates() {
            document.querySelectorAll('.js-total-template').forEach(el => {
                el.textContent = HypothesisFilter.state.productList.length;
            });
        }
    },

    events: {
        init() {
            this.industryChange();

            this.sortChange();

            this.searchChange();

            this.showMore();
        },

        searchChange() {
            const { state, config, filter } = HypothesisFilter;
            const { debounce } = HypothesisFilter.helpers;

            const inputTarget = document.querySelector(config.searchInputTarget);
            if (!inputTarget) return;

            const handleSearch = debounce((e) => {
                state.searchKeyword = e.target.value.trim();
                filter.applyFilters();
            }, 200);

            inputTarget.addEventListener('input', handleSearch);
        },

        industryChange() {
            const { state, config, filter } = HypothesisFilter;
            const { slugify } = HypothesisFilter.helpers;

            const selectTarget = document.querySelector(config.industriesTarget);
            if (!selectTarget) return;

            selectTarget.addEventListener('change', () => {
                const value = selectTarget.value;
                const url = new URL(window.location);

                if (value) {
                    url.searchParams.set(
                        config.filterQueryParam,
                        slugify(value)
                    );
                } else {
                    url.searchParams.delete(config.filterQueryParam);
                }

                history.replaceState({}, '', url);

                state.isCategoryPage = !!value;

                filter.setFilter('industry', value);
            });
        },

        sortChange() {
            const { state, filter } = HypothesisFilter;

            const selectTarget = document.querySelector('#js-tpl-sort');
            if (!selectTarget) return;

            selectTarget.addEventListener('change', (e) => {
                state.currentSort = e.target.value;

                const sortName = document.querySelector('#js-sort-name');
                if (sortName) {
                    const selectedText = e.target.selectedOptions[0].text.trim();
                    sortName.innerHTML = `Sorted by <span class="text-white">${selectedText}</span>`;
                }

                filter.applyFilters();
            });
        },

        showMore() {
            const { state, config, filter, ui } = HypothesisFilter;
            const { slugify } = HypothesisFilter.helpers;

            document.addEventListener('click', (e) => {
                const btn = e.target.closest('.js-show-more');
                if (!btn) return;

                e.preventDefault();

                const industry = btn.dataset.industry;

                if (!state.isCategoryPage) {

                    const url = new URL(window.location);

                    url.searchParams.set(
                        config.filterQueryParam,
                        slugify(industry)
                    );

                    history.replaceState({}, '', url);

                    state.isCategoryPage = true;

                    filter.setFilter('industry', industry);

                    const selectTarget = document.querySelector(config.industriesTarget);

                    if (selectTarget) {
                        const value = [...selectTarget.options]
                            .find(option =>
                                slugify(option.value) ===
                                slugify(industry)
                            )?.value;

                        if (value) {
                            selectTarget.value = value;
                        }
                    }

                    requestAnimationFrame(() => {
                        const target = document.querySelector('#js-product-categories');
                        if (!target) return;

                        window.scrollTo({
                            top: target.getBoundingClientRect().top + window.scrollY - 350,
                            behavior: 'smooth'
                        });
                    });

                    return;
                }

                ui.appendProducts(industry);
            });
        }
    },

    filter: {
        init() {
            const { config, state } = HypothesisFilter;
            const { slugify } = HypothesisFilter.helpers;

            this.filterRemove();
            this.clearFilters();

            const params = new URLSearchParams(window.location.search);
            const industry = params.get(config.filterQueryParam);

            if (industry) {
                const selectTarget = document.querySelector(config.industriesTarget);

                const value = selectTarget
                    ? [...selectTarget.options].find(
                        option => slugify(option.value) === industry
                    )?.value
                    : undefined;

                state.isCategoryPage = true;

                if (value) {
                    selectTarget.value = value;
                    this.setFilter('industry', value);
                } else {
                    // Category không tồn tại → áp dụng filter để hiển thị empty state
                    this.setFilter('industry', industry);
                }

                return;
            }

            state.isCategoryPage = false;
            this.applyFilters();
        },

        getFilteredProducts() {
            const { state } = HypothesisFilter;
            const { slugify, matchesKeyword } = HypothesisFilter.helpers;

            const industryValue = state.filterSelected.find(
                item => item.key === 'industry'
            )?.value || '';

            const industrySlug = slugify(industryValue);

            const keyword = state.searchKeyword.trim();

            return state.productList.filter(product => {

                const industryMatch =
                    !industrySlug ||
                    product.__typeSlug === industrySlug;

                const searchMatch =
                    !keyword ||
                    matchesKeyword(product, keyword);

                return industryMatch && searchMatch;
            });
        },

        applyFilters() {
            const { state, ui } = HypothesisFilter;

            state.visibleItems = {};

            state.filteredProducts = this.sortProducts(
                this.getFilteredProducts(),
                state.currentSort
            );

            ui.renderCategories(state.filteredProducts);

            this.updateTemplateCount(state.filteredProducts.length);
        },

        refreshFilters() {
            const state = HypothesisFilter.state;

            this.renderFilterSelected(state.filterSelected);
            this.applyFilters();
        },

        setFilter(key, value) {
            const { state } = HypothesisFilter;

            const list = state.filterSelected;
            const filteredList = list.filter(item => item.key !== key);

            if (value) filteredList.push({ key, value });

            state.filterSelected = filteredList;

            this.refreshFilters();
        },

        removeFilter(key) {
            const { state, config } = HypothesisFilter;

            state.filterSelected = state.filterSelected.filter(item => item.key !== key);

            if (key === 'industry') {
                state.isCategoryPage = false;

                const selectTarget = document.querySelector(config.industriesTarget);

                if (selectTarget) selectTarget.value = '';

                const url = new URL(window.location);
                url.searchParams.delete(config.filterQueryParam);

                history.replaceState({}, '', url);
            }

            state.searchKeyword = '';
            state.visibleItems = {};

            const searchInput = document.querySelector(config.searchInputTarget);
            if (searchInput) searchInput.value = '';


            this.refreshFilters();
        },

        filterRemove() {
            const container = document.querySelector(HypothesisFilter.config.filterSelected);
            if (!container) return;

            container.addEventListener('click', (e) => {
                const chip = e.target.closest('[data-filter-key]');
                if (!chip) return;

                this.removeFilter(chip.dataset.filterKey);
            });
        },

        clearFilters() {
            const { state, config } = HypothesisFilter;

            document.addEventListener('click', (e) => {
                if (!e.target.closest('#js-clear-filters')) return;

                const selectTarget = document.querySelector(config.industriesTarget);
                if (selectTarget) selectTarget.value = '';

                const searchInput = document.querySelector(config.searchInputTarget);
                if (searchInput) searchInput.value = '';

                const url = new URL(window.location);

                url.searchParams.delete(config.filterQueryParam);

                history.replaceState({}, '', url);

                state.searchKeyword = '';
                state.filterSelected = [];
                state.visibleItems = {};
                state.isCategoryPage = false;

                this.refreshFilters();
            });
        },

        renderFilterSelected(filters) {
            const { config, helpers } = HypothesisFilter

            const container = document.querySelector(config.filterSelected);
            if (!container) return;

            if (!filters.length) {
                container.innerHTML = '';
                return;
            }

            container.innerHTML = filters.map(item => `
                <span class="tpl-results__active-tag" data-filter-key="${item.key}">
                    <span> ${helpers.getDisplayIndustry(item.value)} </span>
                    
                    <button aria-label="Clear filter">
                        <svg width="9" height="9" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M4 4l8 8M12 4l-8 8"></path>
                        </svg>
                    </button>
                </span>
            `).join('');
        },

        updateTemplateCount(count) {
            const countEl = document.querySelector('#js-template-count');
            if (countEl) countEl.textContent = count;
        },

        sortProducts(products, sortType) {
            const sorted = [...products];

            sorted.sort((a, b) => {

                switch (sortType) {

                    case 'created-ascending':
                        return a.__createdAt - b.__createdAt;

                    case 'created-descending':
                        return b.__createdAt - a.__createdAt;

                    case 'best-selling':
                        return (b.__priority - a.__priority)
                            || (b.__createdAt - a.__createdAt);

                    case 'title-ascending':
                        return (a.title || '').localeCompare(b.title || '');

                    case 'title-descending':
                        return (b.title || '').localeCompare(a.title || '');

                    default:
                        return b.__createdAt - a.__createdAt;
                }
            });

            return sorted;
        }
    },

    api: {
        async fetchAllProducts() {
            const CACHE_KEY = 'hypothesis_products_cache';
            const CACHE_TTL = 30 * 60 * 1000;  // Cache 30 phút

            try {
                const cached = sessionStorage.getItem(CACHE_KEY);

                if (cached) {
                    const { data, time } = JSON.parse(cached);

                    if (Date.now() - time < CACHE_TTL) {
                        return data;
                    }
                }

                const products = await this.fetchProducts();
                const { normalizeText, slugify } = HypothesisFilter.helpers;

                products.forEach(product => {
                    product.__titleNormalized = normalizeText(product.title);

                    product.__typeSlug = slugify(product.product_type);

                    product.__createdAt = new Date(product.created_at || 0).getTime();

                    product.__tagsLower = (product.tags || []).map(tag => tag.toLowerCase());

                    product.__priority =
                        product.__tagsLower.includes('recommend') ? 3 :
                            product.__tagsLower.includes('trending') ? 2 :
                                product.__tagsLower.includes('featured') ? 1 :
                                    0;
                });

                sessionStorage.setItem(CACHE_KEY, JSON.stringify({
                    data: products,
                    time: Date.now()
                }));

                return products;

            } catch (error) {
                console.error('Error loading products:', error);
                return [];
            }
        },

        async fetchProducts() {
            const { apiLimit } = HypothesisFilter.config;

            const ids = new Set();
            const products = [];

            let page = 1;

            while (true) {
                const res = await fetch(
                    `${window.location.origin}/collections/hypothesis/products.json?limit=${apiLimit}&page=${page}`
                );

                if (!res.ok) {
                    throw new Error(`Failed to load page ${page}`);
                }

                const { products: items } = await res.json();

                if (!items.length) break;

                items.forEach(product => {
                    if (!ids.has(product.id)) {
                        ids.add(product.id);
                        products.push(product);
                    }
                });

                if (items.length < apiLimit) break;

                page++;
            }

            return products;
        }
    },

    helpers: {
        getDisplayIndustry(industry) {
            return HypothesisFilter.config.industryTitleMap[industry] || industry
        },

        slugify(str) {
            return String(str)
                .trim()
                .toLowerCase()
                .replace(/\s+/g, '-');
        },

        debounce(fn, delay = 200) {
            let timer;
            return (...args) => {
                clearTimeout(timer);
                timer = setTimeout(() => fn(...args), delay);
            };
        },

        getResizeImage(url, width = 640) {
            if (!url) return '';

            const separator = url.includes('?') ? '&' : '?';
            return `${url}${separator}width=${width}`;
        },

        normalizeText(str) {
            return (str || '')
                .toLowerCase()
                .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // bỏ dấu tiếng Việt nếu có
                .replace(/[-_|,.:;!?()[\]{}'"]/g, ' ') // thay ký tự đặc biệt bằng khoảng trắng
                .replace(/\s+/g, ' ')
                .trim();
        },

        matchesKeyword(product, keyword) {
            const words = HypothesisFilter.helpers
                .normalizeText(keyword)
                .split(' ')
                .filter(Boolean);

            if (!words.length) return true;

            return words.every(word => product.__titleNormalized.includes(word));
        }

    },

    async init() {
        const { state, api, events, filter, ui } = this;

        state.productList = await api.fetchAllProducts();
        ui.updateTotalTemplates();

        events.init();
        filter.init(); // filter.init sẽ tự set isCategoryPage dựa trên industry có match hay không
    }
};

HypothesisFilter.init();